'use client'

import Image from 'next/image'
import { Pause, Play } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import { cn } from '@/utilities/ui'

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)'

export function shouldShowHeroVideo({
  hasError,
  hasVideoSrc,
  isIdleReady,
  posterReady,
  prefersReducedMotion,
}: {
  hasError: boolean
  hasVideoSrc: boolean
  isIdleReady: boolean
  posterReady: boolean
  prefersReducedMotion: boolean
}): boolean {
  return hasVideoSrc && !prefersReducedMotion && !hasError && posterReady && isIdleReady
}

export const HERO_VIDEO_START_AFTER_LOAD_MS = 3000

type HeroVideoStartHost = {
  readyState: Document['readyState']
  addEventListener: (type: 'load', listener: () => void, options?: AddEventListenerOptions) => void
  removeEventListener: (type: 'load', listener: () => void) => void
  setTimeout: (handler: () => void, timeout: number) => number
  clearTimeout: (id: number) => void
}

export function scheduleHeroVideoStart(host: HeroVideoStartHost, onReady: () => void): () => void {
  let cancelled = false
  let timerId: number | undefined

  const beginDelay = () => {
    if (cancelled) return
    timerId = host.setTimeout(() => {
      if (!cancelled) onReady()
    }, HERO_VIDEO_START_AFTER_LOAD_MS)
  }

  if (host.readyState === 'complete') {
    beginDelay()
  } else {
    host.addEventListener('load', beginDelay, { once: true })
  }

  return () => {
    cancelled = true
    host.removeEventListener('load', beginDelay)
    if (timerId !== undefined) host.clearTimeout(timerId)
  }
}

export const heroVideoPlaybackButtonClassName = cn(
  'inline-flex h-10 items-center gap-2 rounded-[22px]',
  'border-[1.5px] border-white/20 bg-black/70 px-3.5 py-1.5 font-outfit text-base font-medium text-white',
  'shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
  'disabled:pointer-events-none disabled:opacity-50',
)

type UseHeroVideoBackgroundOptions = {
  desktopSrc?: string
  mobileSrc?: string
  pauseLabel?: string
  playLabel?: string
  poster: string
  posterAlt?: string
}

export function useHeroVideoBackground({
  desktopSrc,
  mobileSrc,
  pauseLabel = 'Pause',
  playLabel = 'Play',
  poster,
  posterAlt = '',
}: UseHeroVideoBackgroundOptions) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isDesktop, setIsDesktop] = useState(true)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [posterReady, setPosterReady] = useState(false)
  const [isIdleReady, setIsIdleReady] = useState(false)
  const [trackedPoster, setTrackedPoster] = useState(poster)
  if (poster !== trackedPoster) {
    setTrackedPoster(poster)
    setPosterReady(false)
    setIsIdleReady(false)
  }

  const hasVideoSrc = Boolean(desktopSrc || mobileSrc)
  const activeSrc = (isDesktop ? desktopSrc : mobileSrc) || desktopSrc || mobileSrc || ''
  const showPosterOnly = !hasVideoSrc || prefersReducedMotion || hasError || !hasStarted
  const showVideo = shouldShowHeroVideo({
    hasError,
    hasVideoSrc,
    isIdleReady,
    posterReady,
    prefersReducedMotion,
  })
  const controlLabel = isPlaying ? pauseLabel : playLabel

  useEffect(() => {
    if (typeof window === 'undefined' || !posterReady) return

    return scheduleHeroVideoStart(
      {
        readyState: document.readyState,
        addEventListener: (type, listener, options) =>
          window.addEventListener(type, listener, options),
        removeEventListener: (type, listener) => window.removeEventListener(type, listener),
        setTimeout: (handler, timeout) => window.setTimeout(handler, timeout),
        clearTimeout: (id) => window.clearTimeout(id),
      },
      () => setIsIdleReady(true),
    )
  }, [posterReady])

  useEffect(() => {
    const desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY)
    const motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)')

    const syncViewport = () => setIsDesktop(desktopMedia.matches)
    const syncMotion = () => setPrefersReducedMotion(motionMedia.matches)

    syncViewport()
    syncMotion()

    desktopMedia.addEventListener('change', syncViewport)
    motionMedia.addEventListener('change', syncMotion)

    return () => {
      desktopMedia.removeEventListener('change', syncViewport)
      motionMedia.removeEventListener('change', syncMotion)
    }
  }, [])

  useEffect(() => {
    setHasError(false)
    setHasStarted(false)
    setIsPlaying(false)
  }, [activeSrc])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !showVideo) return

    video.load()

    const tryPlay = async () => {
      try {
        await video.play()
        setIsPlaying(true)
        setHasStarted(true)
      } catch {
        setIsPlaying(false)
        setHasStarted(false)
      }
    }

    void tryPlay()
  }, [activeSrc, showVideo])

  const togglePlayback = async () => {
    const video = videoRef.current
    if (!video || !hasVideoSrc || prefersReducedMotion || hasError) return

    if (video.paused) {
      try {
        await video.play()
        setIsPlaying(true)
        setHasStarted(true)
      } catch {
        setIsPlaying(false)
      }
      return
    }

    video.pause()
    setIsPlaying(false)
  }

  const handleVideoError = () => {
    setHasError(true)
    setIsPlaying(false)
    setHasStarted(false)
  }

  const handleVideoPause = () => {
    setIsPlaying(false)
  }

  const handleVideoPlaying = () => {
    setIsPlaying(true)
    setHasStarted(true)
  }

  return {
    activeSrc,
    controlLabel,
    handleVideoError,
    handleVideoPause,
    handleVideoPlaying,
    hasError,
    hasVideoSrc,
    isPlaying,
    onPosterReady: () => setPosterReady(true),
    poster,
    posterAlt,
    prefersReducedMotion,
    showPosterOnly,
    showVideo,
    togglePlayback,
    videoRef,
  }
}

type HeroVideoLayerProps = {
  className?: string
  video: ReturnType<typeof useHeroVideoBackground>
}

export function HeroVideoLayer({ className, video }: HeroVideoLayerProps) {
  const {
    handleVideoError,
    handleVideoPause,
    handleVideoPlaying,
    onPosterReady,
    poster,
    posterAlt,
    showPosterOnly,
    showVideo,
    videoRef,
    activeSrc,
  } = video

  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 z-0', className)}>
      <HeroPosterImage alt={posterAlt} onReady={onPosterReady} src={poster} />

      {showVideo ? (
        <video
          key={activeSrc}
          autoPlay
          className={cn('absolute inset-0 size-full object-cover', showPosterOnly && 'opacity-0')}
          loop
          muted
          onError={handleVideoError}
          onPause={handleVideoPause}
          onPlaying={handleVideoPlaying}
          playsInline
          preload="none"
          ref={videoRef}
          src={activeSrc}
        />
      ) : null}
    </div>
  )
}

function HeroPosterImage({
  alt,
  onReady,
  src,
}: {
  alt: string
  onReady: () => void
  src: string
}) {
  return (
    <Image
      alt={alt}
      className="object-cover object-center"
      decoding="sync"
      fetchPriority="high"
      fill
      onError={onReady}
      onLoad={onReady}
      priority
      quality={60}
      sizes="100vw"
      src={src}
    />
  )
}

type HeroVideoPlaybackButtonProps = {
  className?: string
  video: ReturnType<typeof useHeroVideoBackground>
}

export function HeroVideoPlaybackButton({ className, video }: HeroVideoPlaybackButtonProps) {
  const { controlLabel, hasError, hasVideoSrc, isPlaying, prefersReducedMotion, togglePlayback } =
    video

  if (!hasVideoSrc || prefersReducedMotion) {
    return null
  }

  return (
    <button
      aria-pressed={isPlaying}
      className={cn(heroVideoPlaybackButtonClassName, className)}
      disabled={hasError}
      onClick={() => {
        void togglePlayback()
      }}
      type="button"
    >
      {isPlaying ? (
        <Pause aria-hidden className="size-4 shrink-0" />
      ) : (
        <Play aria-hidden className="size-4 shrink-0" />
      )}
      {controlLabel}
    </button>
  )
}

type HeroVideoBackgroundProps = UseHeroVideoBackgroundOptions & {
  className?: string
}

export function HeroVideoBackground(props: HeroVideoBackgroundProps) {
  const { className, ...videoOptions } = props
  const video = useHeroVideoBackground(videoOptions)

  return <HeroVideoLayer className={className} video={video} />
}
